import { draw } from "../deck/draw.js";
import type { ActiveCase, RunState } from "../types/index.js";
import { EXPANSION_STRATEGY_ORG, INVESTMENT_COMMITTEE_ORG, QA_ORG, SHORT_FOCUS_ORG } from "./orgIds.js";
import { hasSmallEliteBonus, ownedCardCount, ownedCardCountById } from "./ownedCards.js";
import { investmentTotals } from "../cards/investments.js";

export type BeginPhaseInput = {
  run: RunState;
  activeCase: ActiveCase;
  extraDraw?: number;
  resetPhase?: boolean;
};

function phaseEnergy(
  organizations: readonly string[],
  nextEnergyModifier: number,
  elite: boolean,
  investmentCommittee: boolean,
): number {
  let energy = 1 + nextEnergyModifier;
  if (organizations.includes(SHORT_FOCUS_ORG)) energy += 1;
  if (elite) energy += 1;
  if (investmentCommittee) energy += 1;
  return Math.max(0, energy);
}

export function beginPhase(input: BeginPhaseInput): RunState {
  const { activeCase } = input;
  let run = { ...input.run };

  const elite = hasSmallEliteBonus(run.organizations, ownedCardCount(run));
  const expansionDraw = run.organizations.includes(EXPANSION_STRATEGY_ORG)
    && ownedCardCountById(run, "intern") >= 15;
  const investments = investmentTotals(activeCase.investments);
  const investmentCommittee = run.organizations.includes(INVESTMENT_COMMITTEE_ORG)
    && (activeCase.investments?.length ?? 0) >= 3;
  const energy = phaseEnergy(run.organizations, activeCase.nextEnergyModifier, elite, investmentCommittee);
  const block = (run.organizations.includes(QA_ORG) ? 1 : 0) + investments.block;
  const phase = input.resetPhase ? 1 : activeCase.phase + 1;

  run = {
    ...run,
    energy,
    block,
    pendingChoice: null,
    pendingRepeat: null,
    actionDrawRemaining: undefined,
  };

  const targetHand = 5 + (input.extraDraw ?? 0) + (elite ? 1 : 0) + (expansionDraw ? 3 : 0) + investments.draw;
  const need = Math.max(0, targetHand - run.hand.length);
  if (need > 0) {
    const drawn = draw(
      { deck: run.deck, discard: run.discard, rngState: run.rngState },
      need,
    );
    run = {
      ...run,
      deck: drawn.deck,
      discard: drawn.discard,
      rngState: drawn.rngState,
      hand: [...run.hand, ...drawn.drawn],
    };
  }

  const discardCount = Math.min(activeCase.nextHandDiscard ?? 0, run.hand.length);
  return {
    ...run,
    pendingChoice: discardCount > 0 ? { kind: "handDisruption", selected: [], count: discardCount } : null,
    activeCase: {
      ...activeCase,
      nextEnergyModifier: 0,
      nextHandDiscard: undefined,
      actionDrawLimit: activeCase.nextActionDrawLimit,
      nextActionDrawLimit: undefined,
      pendingProgress: investments.progress,
      workerProgressDisabled: undefined,
      usedFreeAction: false,
      lastFieldJudgment: null,
      phase,
    },
  };
}
